import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Steps, Button, Input, Flex, Typography, Alert, Spin, Modal } from 'antd';
import {
  IdcardOutlined,
  SafetyCertificateOutlined,
  MobileOutlined,
  CheckCircleOutlined,
  UserOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  PrinterOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import * as abhaApi from '../services/api';
import { 
  extractTxnId, 
  findToken, 
  findProfileData, 
  VisualAbhaCard, 
  OtpInput, 
  downloadAbhaCard 
} from '../components/AbhaFlowComponents';
import miruLogo from '../assets/miru-logo.png';

const { Title, Paragraph, Text } = Typography;

const STEP_LABELS = ['Enter Aadhaar', 'Verify OTP', 'Link Mobile', 'Verify Mobile OTP', 'Create ABHA ID'];
const CREATE_STEPS = [
  { icon: <IdcardOutlined /> },
  { icon: <SafetyCertificateOutlined /> },
  { icon: <MobileOutlined /> },
  { icon: <SafetyCertificateOutlined /> },
  { icon: <CheckCircleOutlined /> },
];

const CreateAbhaPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [aadhaar, setAadhaar] = useState('');
  const [otp, setOtp] = useState('');
  const [mobile, setMobile] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [healthId, setHealthId] = useState('');
  const [txnId, setTxnId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showExistingAlert, setShowExistingAlert] = useState(false);
  const [viewCard, setViewCard] = useState(false);
  const [mobileAlert, setMobileAlert] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [base64Card, setBase64Card] = useState(null);

  const updateTxnId = (res) => {
    const nextId = extractTxnId(res);
    if (nextId) {
      setTxnId(nextId);
      return nextId;
    }
    return txnId;
  };

  const handleViewExisting = () => {
    setShowExistingAlert(false);
    setViewCard(true);
    if (profile && profile.mobileVerified !== true && profile.mobileVerified !== 'true') {
      setMobileAlert(true);
    }
  };

  const handleContinueMobileVerification = () => {
    setMobileAlert(false);
    setViewCard(false);
    setStep(2);
    if (profile?.mobile) {
      setMobile(profile.mobile);
    }
  };

  const handleNext = async () => {
    setError(null);
    setLoading(true);
    try {
      if (step === 0) {
        if (aadhaar.length !== 12) throw new Error('Please enter a valid 12-digit Aadhaar number');
        const res = await abhaApi.generateAadhaarOTP(aadhaar);
        updateTxnId(res);
        setStep(1);
      } else if (step === 1) {
        if (otp.length !== 6) throw new Error('Please enter the 6-digit Aadhaar OTP');
        if (mobile.length !== 10) throw new Error('Please enter your 10-digit mobile number');
        
        const res = await abhaApi.verifyAadhaarOTP(otp, txnId, mobile);
        const nextTxnId = updateTxnId(res) || txnId;
        
        const profileData = findProfileData(res);
        if (profileData && !findToken(profileData)) {
          profileData.token = nextTxnId;
        }

        // Only treat as existing if the profile has actual health fields — not just a token/txnId
        const hasHealthProfile = profileData && (
          profileData.healthId || profileData.healthIdNumber ||
          profileData.ABHANumber || profileData.abhaNumber ||
          profileData.name || profileData.fullName || profileData.firstName
        );
        const isExisting = hasHealthProfile ||
          (res.message && res.message.includes("already exist")) ||
          (res.data?.message && res.data.message.includes("already exist"));

        if (isExisting) {
          setProfile(profileData);
          setShowExistingAlert(true);
        } else {
          setStep(2);
        }
      } else if (step === 2) {
        if (mobile.length !== 10) throw new Error('Please enter a valid 10-digit mobile number');
        const res = await abhaApi.generateMobileOTP(mobile, txnId);
        updateTxnId(res);
        setStep(3);
      } else if (step === 3) {
        if (mobileOtp.length !== 6) throw new Error('Please enter the 6-digit Mobile OTP');
        const res = await abhaApi.verifyMobileOTP(mobileOtp, txnId);
        updateTxnId(res);
        setStep(4);
      } else if (step === 4) {
        if (!healthId) throw new Error('Please choose an ABHA address');
        if (healthId.length < 4) throw new Error('ABHA address must be at least 4 characters long');
        
        const fullHealthId = healthId.includes('@') ? healthId : `${healthId}@sbx`;
        const res = await abhaApi.createHealthID(txnId, fullHealthId);
        const profileData = findProfileData(res);
        if (profileData && !findToken(profileData)) {
          profileData.token = txnId;
        }
        setProfile(profileData);
        setSuccess(true);
      }
    } catch (err) {
      console.error('[ABHA ERROR]', err);
      if (typeof err === 'object' && !err.message && Object.keys(err).length > 0) {
        const details = Object.entries(err)
          .map(([key, val]) => `${key}: ${val}`)
          .join(', ');
        setError(details || 'Action failed');
      } else {
        setError(err.message || 'Action failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 20px' }}>
      <div style={{ maxWidth: 500, margin: '0 auto', background: '#fff', borderRadius: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '24px 32px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
          <img 
            src={miruLogo} 
            alt="MiRu" 
            style={{ height: 60, marginBottom: 16, cursor: 'pointer' }} 
            onClick={() => navigate('/')}
          />
          
          <Title level={3} style={{ margin: 0 }}>Create ABHA Health ID</Title>
        </div>

        <div style={{ padding: '32px' }}>
          {success || viewCard ? (
            <div className="py-4">
              <div className="text-center mb-6">
                <CheckCircleOutlined style={{ fontSize: 48, color: '#12B76A' }} />
                <Title level={3} style={{ marginTop: 12 }}>{success ? 'ABHA Created!' : 'ABHA Profile'}</Title>
              </div>
              
              <VisualAbhaCard profile={profile} onCardLoad={setBase64Card} txnId={txnId} />

              <Flex vertical gap={12} className="mt-6">
                <Button 
                  type="primary"
                  icon={<DownloadOutlined />} 
                  size="large"
                  style={{ borderRadius: 12, background: '#6B35D4', borderColor: '#6B35D4' }} 
                  onClick={() => downloadAbhaCard(base64Card, profile)}
                  disabled={!base64Card}
                >
                  Download ABHA Card
                </Button>
                <Button 
                  icon={<PrinterOutlined />} 
                  size="large"
                  style={{ borderRadius: 12 }}
                  onClick={() => window.print()}
                >
                  Print ABHA
                </Button>
                <Button 
                  size="large" 
                  variant="text"
                  onClick={() => navigate('/')}
                >
                  Go to Home
                </Button>
              </Flex>

              {mobileAlert && (
                <Modal
                  title={<Flex align="center" gap={8}><InfoCircleOutlined style={{ color: '#E45D25' }} /> <span>ALERT!</span></Flex>}
                  open={mobileAlert}
                  onCancel={() => setMobileAlert(false)}
                  centered
                  footer={[
                    <Button key="later" onClick={() => setMobileAlert(false)}>Maybe Later</Button>,
                    <Button key="continue" type="primary" onClick={handleContinueMobileVerification} style={{ background: '#E45D25', borderColor: '#E45D25' }}>Continue</Button>
                  ]}
                >
                  <div className="text-center py-4">
                    <Paragraph strong style={{ fontSize: 16 }}>The mobile number linked to this ABHA is not verified.</Paragraph>
                    <Paragraph type="secondary">Please verify your mobile number to continue using all features of ABHA.</Paragraph>
                  </div>
                </Modal>
              )}
            </div>
          ) : (
            <div>
              <Steps current={step} items={CREATE_STEPS} size="small" className="mb-6" />
              <p className="text-center text-xs text-slate-500 mb-6">
                Step {step + 1} of {CREATE_STEPS.length} &nbsp;·&nbsp; <span className="font-semibold text-purple-700">{STEP_LABELS[step]}</span>
              </p>
              
              <Spin spinning={loading}>
                <div style={{ minHeight: 200 }}>
                  {error && <Alert title={error} type="error" showIcon closable className="mb-4" />}
                  
                  {step === 0 && (
                    <div>
                      <Text strong>Aadhaar Number</Text>
                      <Input
                        size="large"
                        placeholder="1234 5678 9012"
                        value={aadhaar}
                        onChange={e => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
                        prefix={<IdcardOutlined style={{ color: '#6B35D4' }} />}
                        style={{ marginTop: 8, borderRadius: 10 }}
                      />
                      <p className="text-xs mt-2 text-slate-400">OTP will be sent to your Aadhaar-linked mobile.</p>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      {showExistingAlert && (
                        <Alert
                          title="ABHA account exist"
                          description="An ABHA profile is already associated with this Aadhaar number."
                          type="warning"
                          showIcon
                          action={
                            <Button size="small" type="primary" onClick={handleViewExisting} style={{ background: '#6B35D4', borderColor: '#6B35D4' }}>
                              View Profile
                            </Button>
                          }
                          className="mb-6"
                        />
                      )}
                      <div className="text-center mb-6">
                        <Title level={5} style={{ marginBottom: 4 }}>Confirm OTP</Title>
                        <Paragraph type="secondary">OTP sent to Aadhaar registered mobile number</Paragraph>
                        <OtpInput value={otp} onChange={setOtp} />
                      </div>
                      
                      <div className="mt-8">
                        <Text strong>Mobile Number<span style={{ color: '#ff4d4f' }}>*</span></Text>
                        <Input
                          size="large"
                          placeholder="Mobile number"
                          value={mobile}
                          onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          addonBefore="+91"
                          style={{ marginTop: 8 }}
                          disabled={showExistingAlert}
                        />
                        <Alert
                          className="mt-4"
                          type="info"
                          showIcon
                          icon={<InfoCircleOutlined style={{ color: '#6B35D4' }} />}
                          title={
                            <span style={{ fontSize: 12, color: '#6B35D4' }}>
                              It is preferable to use your Aadhaar linked mobile number.
                            </span>
                          }
                          style={{ backgroundColor: '#f9f7ff', border: 'none' }}
                        />
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="text-center py-4">
                      <Title level={5}>Link Mobile</Title>
                      <Paragraph>Click continue to send an OTP to <strong>+91-{mobile}</strong> for verification.</Paragraph>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="text-center py-4">
                      <Text strong className="block mb-4">Mobile OTP</Text>
                      <OtpInput value={mobileOtp} onChange={setMobileOtp} />
                      <Paragraph className="mt-4">Sent to +91-{mobile}</Paragraph>
                    </div>
                  )}

                  {step === 4 && (
                    <div>
                      <Text strong>Create ABHA Address</Text>
                      <Input
                        size="large"
                        placeholder="e.g. rahul.kumar"
                        value={healthId}
                        onChange={e => setHealthId(e.target.value.toLowerCase().replace(/[^a-z0-9.]/g, ''))}
                        prefix={<UserOutlined style={{ color: '#6B35D4' }} />}
                        addonAfter="@sbx"
                        style={{ marginTop: 8, borderRadius: 10 }}
                      />
                      <p className="text-xs mt-2 text-slate-400">Min 4 chars. Only lowercase, numbers and dots allowed.</p>
                    </div>
                  )}
                </div>
              </Spin>

              <Flex justify="space-between" align="center" className="mt-10">
                <Button 
                  icon={<ArrowLeftOutlined />}
                  onClick={() => step === 0 ? navigate('/') : setStep(s => s - 1)}
                  disabled={loading}
                  type="text"
                >
                  {step === 0 ? 'Home' : 'Back'}
                </Button>
                
                {!showExistingAlert && (
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={handleNext} 
                    loading={loading} 
                    style={{ 
                      background: '#6B35D4', 
                      borderColor: '#6B35D4',
                      borderRadius: 12,
                      paddingInline: 32,
                      height: 48,
                      fontWeight: 600
                    }}
                  >
                    {step === 4 ? 'Create ABHA' : 'Continue'}
                  </Button>
                )}
              </Flex>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAbhaPage;
